import {
  isConnectorRouteAuth,
  isRouteType,
  type RouteType,
} from "./stepConfig";

export type RouteAuthMode =
  | "public"
  | "external_id"
  | "space"
  | "tenant"
  | "sso"
  | "connected_app";

export const ROUTE_AUTH_MODES: RouteAuthMode[] = [
  "public",
  "external_id",
  "tenant",
  "sso",
  "connected_app",
  "space",
];

export type RouteTypeBucket = RouteType | "untyped";

export const ROUTE_TYPE_BUCKETS: RouteTypeBucket[] = [
  "webpage",
  "api",
  "webhook",
  "other",
  "untyped",
];

function routeTypeBucket(t: RouteType | null | undefined): RouteTypeBucket {
  return t ?? "untyped";
}

export type MonitorRouteWorkflowInput = {
  id: string;
  name: string;
  spaceId: string;
  routes: ReadonlyArray<{
    route: string;
    stepDir: string;
    routeType: string | null;
    routeAuth: string | null;
  }>;
};

export type MonitorRouteRow = {
  workflowId: string;
  workflowName: string;
  spaceId: string;
  route: string;
  stepDir: string;
  routeType: RouteType | null;
  routeAuth: RouteAuthMode;
};

export function normalizeRouteAuth(
  raw: string | null | undefined
): RouteAuthMode {
  if (raw === "public") return "public";
  if (raw === "external_id") return "external_id";
  if (raw === "tenant") return "tenant";
  if (raw === "sso") return "sso";
  if (isConnectorRouteAuth(raw ?? undefined)) return "connected_app";
  return "space";
}

export function computeMonitorRoutes(
  workflows: ReadonlyArray<MonitorRouteWorkflowInput>
): MonitorRouteRow[] {
  const rows: MonitorRouteRow[] = [];

  for (const workflow of workflows) {
    for (const route of workflow.routes) {
      rows.push({
        workflowId: workflow.id,
        workflowName: workflow.name,
        spaceId: workflow.spaceId,
        route: route.route,
        stepDir: route.stepDir,
        routeType: isRouteType(route.routeType) ? route.routeType : null,
        routeAuth: normalizeRouteAuth(route.routeAuth),
      });
    }
  }

  return rows.sort(
    (a, b) =>
      ROUTE_AUTH_MODES.indexOf(a.routeAuth) -
        ROUTE_AUTH_MODES.indexOf(b.routeAuth) ||
      a.route.localeCompare(b.route) ||
      a.workflowName.localeCompare(b.workflowName)
  );
}

export type MonitorWorkflowRouteSummary = {
  workflowId: string;
  workflowName: string;
  spaceId: string;
  routes: MonitorRouteRow[];
};

export function groupRoutesByWorkflow(
  rows: ReadonlyArray<MonitorRouteRow>
): MonitorWorkflowRouteSummary[] {
  const byId = new Map<string, MonitorWorkflowRouteSummary>();
  for (const row of rows) {
    const existing = byId.get(row.workflowId);
    if (existing) {
      existing.routes.push(row);
      continue;
    }
    byId.set(row.workflowId, {
      workflowId: row.workflowId,
      workflowName: row.workflowName,
      spaceId: row.spaceId,
      routes: [row],
    });
  }
  return Array.from(byId.values()).sort((a, b) =>
    a.workflowName.localeCompare(b.workflowName)
  );
}

export function matchingRoutes(
  summary: MonitorWorkflowRouteSummary,
  authFilter: RouteAuthMode | null
): MonitorRouteRow[] {
  if (!authFilter) return [...summary.routes];
  return summary.routes.filter((row) => row.routeAuth === authFilter);
}

export function filterWorkflowSummaries(
  summaries: ReadonlyArray<MonitorWorkflowRouteSummary>,
  authFilter: RouteAuthMode | null
): MonitorWorkflowRouteSummary[] {
  return summaries.filter(
    (summary) => matchingRoutes(summary, authFilter).length > 0
  );
}

export type WorkflowRouteSort = "name" | "routes";

export function sortWorkflowRouteSummaries(
  summaries: ReadonlyArray<MonitorWorkflowRouteSummary>,
  sort: WorkflowRouteSort,
  authFilter: RouteAuthMode | null
): MonitorWorkflowRouteSummary[] {
  const list = [...summaries];
  list.sort((a, b) => {
    if (sort === "routes") {
      const aCount = matchingRoutes(a, authFilter).length;
      const bCount = matchingRoutes(b, authFilter).length;
      return bCount - aCount || a.workflowName.localeCompare(b.workflowName);
    }
    return a.workflowName.localeCompare(b.workflowName);
  });
  return list;
}

type MonitoringRouteAuthSummary = {
  workflowCount: number;
  routeTypeCounts: Record<RouteTypeBucket, number>;
};

export type MonitoringRouteSummary = {
  workflowCount: number;
  byAuth: Record<RouteAuthMode, MonitoringRouteAuthSummary>;
};

export function emptyRouteSummary(): MonitoringRouteSummary {
  return {
    workflowCount: 0,
    byAuth: Object.fromEntries(
      ROUTE_AUTH_MODES.map((mode) => [
        mode,
        {
          workflowCount: 0,
          routeTypeCounts: Object.fromEntries(
            ROUTE_TYPE_BUCKETS.map((bucket) => [bucket, 0])
          ) as Record<RouteTypeBucket, number>,
        },
      ])
    ) as Record<RouteAuthMode, MonitoringRouteAuthSummary>,
  };
}

export type MonitorRouteGroup = {
  workflowId: string;
  routeAuth: string | null;
  routeType: string | null;
  routeCount: number;
};

export function summarizeRouteGroups(
  groups: ReadonlyArray<MonitorRouteGroup>
): MonitoringRouteSummary {
  const summary = emptyRouteSummary();
  const workflowsByAuth = new Map<RouteAuthMode, Set<string>>(
    ROUTE_AUTH_MODES.map((mode) => [mode, new Set<string>()])
  );
  const workflowsWithRoutes = new Set<string>();

  for (const group of groups) {
    const mode = normalizeRouteAuth(group.routeAuth);
    const bucket = routeTypeBucket(
      isRouteType(group.routeType) ? group.routeType : null
    );
    summary.byAuth[mode].routeTypeCounts[bucket] += group.routeCount;
    workflowsByAuth.get(mode)!.add(group.workflowId);
    workflowsWithRoutes.add(group.workflowId);
  }

  for (const mode of ROUTE_AUTH_MODES) {
    summary.byAuth[mode].workflowCount = workflowsByAuth.get(mode)!.size;
  }
  summary.workflowCount = workflowsWithRoutes.size;
  return summary;
}
